<script setup>
  import { computed, onMounted, ref } from 'vue';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { useI18n } from 'vue-i18n';
  import Modal from '../../components/ui/Modal.vue';
  import Transfer from '../../pages/cart/Transfer.vue';
  import { createReusableTemplate } from '@vueuse/core'
  import ChangeSubBilligAccount from '../../pages/cart/ChangeSubBilligAccount.vue';

  const [DefineTemplate, ReuseTemplate] = createReusableTemplate()
  const { t } = useI18n();
  const emits = defineEmits(['toogleRefundBox']);
  const cartStore = useBrowseCartStore();
  const showSubBillingModal = ref(false);
  const showModalMove = ref(false);

  const title = computed(() => {
    if(cartStore.cart) {
      return cartStore.cart.name
    }
  });

  const policy = computed(() => {
    if (cartStore.cart) {
      return cartStore.cart.policy;
    }
  });

  const cartId = computed(() => {
    if (cartStore.cart) {
      return cartStore.cart.id;
    }
  });

  const subLibraries = computed(() => {
    if (cartStore.cart) {
      return cartStore.cart.sub_libraries;
    }
  })

  const libraryId = computed(() => {
    if (cartStore.cart) {
      return cartStore.cart.library.id;
    }
  });

  const exportUrl = (format = 'csv') => {
    window.location.href = `/admin/libraries/${libraryId.value}/carts/${cartId.value}/export.${format}`;
  }

  const cart = computed(() => {
    return cartStore.cart;
  });

  const destroyCart = async (event) => {
    event.preventDefault();
    if (confirm(t('cart.cart_destroy_confirm'))) {
      await cartStore.destroyCart(cartId.value);
      window.location.href = '/carts';
    }
  }

  const reprocessCart = (event) => {
    event.preventDefault();
    if (confirm(t('cart.cart_reprocess_confirm'))) {
      cartStore.reprocessCart(cartId.value);
    }
  }

  const unlockCart = (event) => {
    event.preventDefault();
    if (confirm(t('cart.cart_unlock_confirm'))) {
      cartStore.unlockCart(cartId.value);
    }
  }

  const resendEmail = (event) => {
    event.preventDefault();
    if(confirm(t('cart.resend_email'))) {
      cartStore.resendEmail(cartId.value);
    }
  }

  const showSubAccountModal = (event) => {
    event.preventDefault();
    showSubBillingModal.value = true;
  }

  const goTo = (url) => {
    window.location.href = url;
  }

  onMounted(async () => {
    while (!cartStore.cart) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  });

</script>
<template>

  <DefineTemplate v-slot="{ title, clickAction = null, icon }">
    <button
      type="button"
      data-bs-toggle="tooltip" :data-bs-title="title"
      v-on="clickAction != null ? { click: clickAction } : {}"
      class="btn btn-primary">
      <i :class="`bi ${icon}`" aria-hidden="true"></i>
      <span class="sr-only">{{ title }}</span>
    </button>
  </DefineTemplate>

  <Modal :show="showSubBillingModal" @close="showSubBillingModal = false" :withOutTitle="true">
    <ChangeSubBilligAccount :cartId="cartStore.cart.id" @closeModal="showSubBillingModal = false" />
  </Modal>
  <section class="container-fluid">
    <div class="row align-items-center mt-3">
      <div class="col">
        <ul class="nav">
          <li class="nav-item">
            <a class="nav-link" href="/carts">
              <i class="bi bi-chevron-left"></i>
              {{ t('cart.back_button_carts') }}
            </a>
          </li>
        </ul>
      </div>

      <div class="col text-center">
        <h1 id="cart-name">{{ title }}</h1>
      </div>

      <div class="col text-end">
        <div v-if="policy">
          <!-- FBKS-3962: with the cart summary, actions sit on the right of the filters toolbar (CartGridHeader) -->
          <Teleport defer to="#cart-toolbar-actions" :disabled="!cart.cart_content_summary">
          <div class="btn-group" role="group" :aria-label="t('cart.actions.title')">
            <template v-if="policy.cart_editable">
              <ReuseTemplate
                :clickAction="() => goTo(`/carts/${cartId}/edit`)"
                :title="t('cart.actions.edit')"
                :icon="'bi-pen'"
              />

              <ReuseTemplate
                :clickAction="() => goTo(`/carts/${cartId}/import/new`)"
                :title="t('cart.actions.import_isbns')"
                :icon="'bi-file-earmark-arrow-down'"
              />

              <ReuseTemplate
                :clickAction="() => showModalMove = true"
                :title="t('cart.actions.move_cart')"
                :icon="'bi-file-earmark-minus'"
                v-if="policy.cart_movable"
              />
            </template>

            <ReuseTemplate
              :clickAction="unlockCart"
              :title="t('cart.actions.unlock')"
              :icon="'bi-unlock'"
              v-if="((policy.admin || policy.support) && cart.library.active) && cart.status === 'in_processing'"
            />

            <ReuseTemplate
              :clickAction="() => goTo(`/carts/${cartId}/duplicate`)"
              :title="t('cart.actions.duplicate_cart')"
              :icon="'bi-copy'"
            />

            <ReuseTemplate
              :clickAction="() => emits('toogleRefundBox')"
              :title="t('cart.actions.process_a_refund')"
              :icon="'bi-cash-stack'"
              v-if="(cart.validated || cart.failed) && (policy.admin || policy.support)"
            />

            <ReuseTemplate
              :clickAction="reprocessCart"
              :title="t('cart.actions.reprocess')"
              :icon="'bi-arrow-clockwise'"
              v-if="((cart.validated || cart.failed) && (policy.admin || policy.support) && cart.library.active) && cart.status !== 'to_process'"
            />

            <ReuseTemplate
              :clickAction="resendEmail"
              :title="t('cart.actions.resend_email')"
              :icon="'bi-envelope-arrow-up'"
              v-if="policy.cart_resendable"
            />

            <ReuseTemplate
              :clickAction="showSubAccountModal"
              :title="t('cart.actions.edit_sub_account')"
              :icon="'bi-envelope-at'"
              v-if="cart.validated && (policy.admin || policy.support) && subLibraries.length > 0"
            />

            <ReuseTemplate
              :clickAction="destroyCart"
              :title="t('cart.actions.delete')"
              :icon="'bi-trash'"
              v-if="policy.cart_deletable"
            />

            <ReuseTemplate
              :clickAction="() => exportUrl('csv')"
              :title="t('cart.actions.export_csv')"
              :icon="'bi-filetype-csv'"
            />

            <ReuseTemplate
              :clickAction="() => exportUrl('xlsx')"
              :title="t('cart.actions.export_xlsx')"
              :icon="'bi-filetype-xlsx'"
              v-if="cart.validated"
            />

            <ReuseTemplate
              :clickAction="() => goTo(`/carts/${cartId}/logs`)"
              :title="t('cart.actions.show_processing_logs')"
              :icon="'bi-file-earmark-medical'"
              v-if="policy.logs && policy.cart_processing_logs"
            />
          </div>
          </Teleport>
        </div>
      </div>
    </div>
  </section>

  <Modal :show="showModalMove" @close="showModalMove = false">
    <template #title>{{ t('cart.move_cart.header') }}</template>
    <Transfer :cartId="cartId" @close="showModalMove = false" />
  </Modal>
</template>

<style scoped>
  .button--link-cancel {
    color: #9F0022 !important;
  }

  :deep() .mkt-modal {
    overflow: visible !important;
    max-height: 500px !important;
  }
  :deep() .modal-guts {
    overflow: visible !important;
  }
</style>
